import React from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";

import Input from "../../form/Input";
import useAuth from "../../../hooks/useAuth";

const validationSchema = Yup.object().shape({
  list: Yup.array().of(
    Yup.object().shape({
      amount: Yup.number()
        .typeError("Required")
        .required("Required"),
      notes: Yup.string().nullable(),
    })
  ),
});

const initialValue = { amount: "", notes: "" };

export default function OneTimeChargesModal({
  shipment,
  open = true,
  onClose,
  onSaved,
  onSubmit,
  loading = false,
}) {
  const { user } = useAuth();
  const isInternalStaff = ["admin", "manager", "inventory"].includes(user?.role);
  const initialValues = {
    list: Array.isArray(shipment?.oneTimeCharges?.list)
      ? shipment.oneTimeCharges.list
      : [initialValue],
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={22} fontWeight={500} color="text.primary" align="center">
          One Time Flat Charges
        </Typography>
        <IconButton onClick={onClose} size="large" sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <Form style={{ width: "100%" }}>
              <FieldArray name="list">
                {({ push, remove }) => (
                  <Box mt={1} display="flex" flexDirection="column" gap={2}>
                    {values.list.map((charge, index) => (
                      <Grid container spacing={1.5} alignItems="center" key={index}>
                        <Grid size={isInternalStaff ? 7 : 8}>
                          <Input
                            fullWidth
                            type="autoExpand"
                            showNAButton={false}
                            name={`list.${index}.notes`}
                            label="Charge Description"
                            disabled={!isInternalStaff}
                          />
                        </Grid>
                        <Grid size={isInternalStaff ? 4 : 4}>
                          <Input
                            fullWidth
                            name={`list.${index}.amount`}
                            label="Charge Amount"
                            type="number"
                            step={0.01}
                            showNAButton={false}
                            disabled={!isInternalStaff}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </Grid>
                        {isInternalStaff && (
                          <Grid size={1}>
                            <IconButton
                              disabled={values.list.length === 1 || loading}
                              onClick={() => remove(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    ))}
                    {isInternalStaff && (
                      <>
                        <Box>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => push(initialValue)}
                            disabled={loading}
                          >
                            Add More
                          </Button>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                            startIcon={loading && <CircularProgress size={20} />}
                          >
                            Save
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </FieldArray>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
